import "jspdf-autotable";
import { pdfHeader } from "@/utilities/pdfUtils";

/**
 * Generate a PDF listing all sibling (brothers & sisters) groups.
 *
 * @param {Object} options
 * @param {Array} options.groups - Sibling groups from GET /parents/siblings
 * @param {Object} options.settings - App settings (for pdfHeader)
 * @param {Function} options.t - i18n translate function
 * @param {string} options.locale - Current locale
 * @param {string} [options.nonInscritLabel] - Label when student has no class
 */
export const generateSiblingsListPdf = ({
  groups,
  settings,
  t,
  locale = "fr-FR",
  nonInscritLabel = "",
}) => {
  const isArabic = locale === "ar-AR";
  const reverseIfArabic = (arr) => (isArabic ? arr.slice().reverse() : arr);

  const doc = pdfHeader(settings, {
    title: t("liste.freresSoeurs"),
    locale,
  });

  const headers = reverseIfArabic([
    t("liste.studentFullName"),
    t("liste.brotherSister"),
    t("liste.classe"),
  ]);

  const body = [];
  let studentCount = 0;

  groups.forEach((group) => {
    const students = group.students || [];
    if (students.length === 0) return;

    const parentName = group.parentName || "--";

    // Group container header — parent name spans all columns
    body.push([
      {
        content: `${t("liste.parentFullName")}: ${parentName}`,
        colSpan: 3,
        styles: {
          fontStyle: "bold",
          halign: "center",
          valign: "middle",
          fillColor: [220, 220, 220],
          textColor: 0,
          fontSize: 10,
        },
      },
    ]);

    students.forEach((student) => {
      const genderLabel =
        student.gender === "female"
          ? t("liste.sister")
          : t("liste.brother");

      let classLabel = student.className || nonInscritLabel || "--";
      if (student.classroomType === "affectation" && student.className) {
        classLabel = `${student.className} - ${t(
          "etablissement.niveauAffectation"
        )}`;
      }

      body.push(
        reverseIfArabic([
          student.fullName || "--",
          genderLabel,
          classLabel,
        ])
      );
      studentCount += 1;
    });
  });

  doc.autoTable({
    theme: "grid",
    styles: {
      font: "Amiri",
      halign: isArabic ? "right" : "left",
      fontSize: 9,
      valign: "middle",
    },
    headStyles: {
      fillColor: [30, 64, 175],
      textColor: 255,
      font: "Amiri",
      halign: "center",
    },
    startY: 45,
    head: [headers],
    body,
  });

  const pages = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  doc.setFontSize(8);

  for (let j = 1; j <= pages; j++) {
    doc.setPage(j);
    doc.text(
      t("pdf.pageNumber", { current: j, total: pages }),
      pageWidth / 2,
      pageHeight - 10,
      { align: "center" }
    );
  }

  doc.save(`${t("liste.freresSoeurs")}.pdf`);

  return { studentCount, groupCount: groups.length };
};
